import React, { useState, useEffect } from 'react';
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Chip,
  Box,
  Typography,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  TextField,
  Grid
} from '@mui/material';
import { reportAPI } from '../../services/api';
import toast from 'react-hot-toast';
import moment from 'moment';

const ReportList = () => {
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedReport, setSelectedReport] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [action, setAction] = useState('');
  const [filterStatus, setFilterStatus] = useState('pending');
  const [analyticsData, setAnalyticsData] = useState(null);
  const [dateRange, setDateRange] = useState({
    start_date: moment().subtract(30, 'days').format('YYYY-MM-DD'),
    end_date: moment().format('YYYY-MM-DD')
  });

  useEffect(() => {
    fetchReports();
  }, [filterStatus]);

  const fetchReports = async () => {
    setLoading(true);
    try {
      const response = await reportAPI.getUserReports(filterStatus);
      setReports(response.data.data);
    } catch (error) {
      toast.error('Failed to fetch reports');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateReport = async () => {
    try {
      await reportAPI.updateReport(selectedReport.id, 'reviewed', action);
      toast.success(`Report ${action === 'block' ? 'blocked' : 'reviewed'} successfully`);
      setDialogOpen(false);
      fetchReports();
    } catch (error) {
      toast.error('Failed to update report');
    }
  };

  const handleGenerateAnalytics = async () => {
    try {
      const response = await reportAPI.generateAnalytics(dateRange);
      setAnalyticsData(response.data.data);
    } catch (error) {
      toast.error('Failed to generate analytics');
    }
  };

  const openActionDialog = (report, actionType) => {
    setSelectedReport(report);
    setAction(actionType);
    setDialogOpen(true);
  };

  const getReasonColor = (reason) => {
    const colors = {
      fake_profile: 'error',
      inappropriate: 'warning',
      harassment: 'error',
      scam: 'error',
      other: 'info'
    };
    return colors[reason] || 'default';
  };

  return (
    <Box>
      <Typography variant="h4" fontWeight="bold" mb={3}>
        User Reports
      </Typography>

      <Grid container spacing={3}>
        <Grid item xs={12} md={8}>
          <Paper sx={{ mb: 3, p: 2 }}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
              <FormControl size="small" sx={{ minWidth: 150 }}>
                <InputLabel>Filter by Status</InputLabel>
                <Select
                  value={filterStatus}
                  label="Filter by Status"
                  onChange={(e) => setFilterStatus(e.target.value)}
                >
                  <MenuItem value="pending">Pending</MenuItem>
                  <MenuItem value="reviewed">Reviewed</MenuItem>
                  <MenuItem value="dismissed">Dismissed</MenuItem>
                </Select>
              </FormControl>
              <Button variant="contained" onClick={fetchReports}>
                Refresh
              </Button>
            </Box>

            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>ID</TableCell>
                    <TableCell>Reporter</TableCell>
                    <TableCell>Reported User</TableCell>
                    <TableCell>Reason</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Date</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loading ? (
                    <TableRow>
                      <TableCell colSpan={7} align="center">
                        <CircularProgress />
                      </TableCell>
                    </TableRow>
                  ) : reports.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} align="center">
                        No reports found
                      </TableCell>
                    </TableRow>
                  ) : (
                    reports.map((report) => (
                      <TableRow key={report.id}>
                        <TableCell>{report.id}</TableCell>
                        <TableCell>
                          <Box>
                            <Typography variant="body2">
                              {report.reporter_name || 'N/A'}
                            </Typography>
                            <Typography variant="caption" color="textSecondary">
                              {report.reporter_phone}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Box>
                            <Typography variant="body2">
                              {report.reported_name || 'N/A'}
                            </Typography>
                            <Typography variant="caption" color="textSecondary">
                              {report.reported_phone}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Chip 
                            label={report.reason?.replace('_', ' ')} 
                            color={getReasonColor(report.reason)}
                            size="small"
                          />
                          {report.description && (
                            <Typography variant="caption" display="block" color="textSecondary" mt={1}>
                              {report.description}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          <Chip 
                            label={report.status}
                            color={report.status === 'pending' ? 'warning' : report.status === 'reviewed' ? 'success' : 'default'}
                            size="small"
                          />
                        </TableCell>
                        <TableCell>{moment(report.created_at).format('YYYY-MM-DD')}</TableCell>
                        <TableCell>
                          {report.status === 'pending' && (
                            <>
                              <Button
                                size="small"
                                color="warning"
                                onClick={() => openActionDialog(report, 'warn')}
                              >
                                Warn
                              </Button>
                              <Button
                                size="small"
                                color="error"
                                onClick={() => openActionDialog(report, 'block')}
                              >
                                Block
                              </Button>
                              <Button
                                size="small"
                                onClick={() => openActionDialog(report, 'dismiss')}
                              >
                                Dismiss
                              </Button>
                            </>
                          )}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Grid>

        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Analytics Report
            </Typography>
            <TextField
              fullWidth
              type="date"
              label="Start Date"
              value={dateRange.start_date}
              onChange={(e) => setDateRange({ ...dateRange, start_date: e.target.value })}
              margin="normal"
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              fullWidth
              type="date"
              label="End Date"
              value={dateRange.end_date}
              onChange={(e) => setDateRange({ ...dateRange, end_date: e.target.value })}
              margin="normal"
              InputLabelProps={{ shrink: true }}
            />
            <Button
              fullWidth
              variant="contained"
              onClick={handleGenerateAnalytics}
              sx={{ mt: 2 }}
            >
              Generate Report
            </Button>

            {analyticsData && (
              <Box sx={{ mt: 3 }}>
                <Typography variant="subtitle1" gutterBottom>
                  User Growth: {analyticsData.userGrowth?.length || 0} entries
                </Typography>
                <Typography variant="subtitle1" gutterBottom>
                  Total Verifications: {analyticsData.verificationStats?.total_verifications || 0}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  Approved: {analyticsData.verificationStats?.approved || 0}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  Rejected: {analyticsData.verificationStats?.rejected || 0}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  Pending: {analyticsData.verificationStats?.pending || 0}
                </Typography>
              </Box>
            )}
          </Paper>
        </Grid>
      </Grid>

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)}>
        <DialogTitle>
          {action === 'block' ? 'Block User' : action === 'warn' ? 'Warn User' : 'Dismiss Report'}
        </DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to {action === 'block' ? 'block this user' : action === 'warn' ? 'send a warning' : 'dismiss this report'}?
          </Typography>
          {action === 'block' && (
            <Typography color="error" variant="body2" sx={{ mt: 1 }}>
              This will block the reported user from accessing the platform.
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleUpdateReport} color={action === 'block' ? 'error' : 'primary'} variant="contained">
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ReportList;